import {
  CrmFieldService,
  StandardIds,
  SystemFieldIds,
} from '@galaxy/crm/services/field-service';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  Input,
  Signal,
  WritableSignal,
  computed,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { ActivityInterface } from '@vendasta/crm';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import {
  Observable,
  of,
} from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CollapsibleSectionComponent } from '../../collapsible-section/collapsible-section.component';
import { TranslationModule } from '../../../i18n/translation-module';
import { ActivityRichTextComponent } from './activity-rich-text.component';

@Component({
  selector: 'crm-activity-logged-call',
  templateUrl: './logged-call.component.html',
  styleUrls: ['./logged-call.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, TranslateModule, TranslationModule, CollapsibleSectionComponent, ActivityRichTextComponent],
})
export class CrmActivityLoggedCallComponent {
  dateFormat = DateFormat.mediumDateShortTime;
  callRecordingSource$: Observable<any>;

  private _activity: WritableSignal<ActivityInterface> = signal({});
  @Input({ required: true }) set activity(activity: ActivityInterface) {
    this._activity.set(activity);
  }

  get activity() {
    return this._activity();
  }

  cardInfo: Signal<ActivityCardInformation> = computed(() => {
    const cardInfo: any = {
      note: this.getFieldData(this._activity(), StandardIds.ActivityCallBody),
      direction: this.getFieldData(this._activity(), StandardIds.ActivityCallDirection),
      status: this.getFieldData(this._activity(), StandardIds.ActivityCallStatus),
      outcome: this.getFieldData(this._activity(), StandardIds.ActivityCallOutcome),
      date:
        this.fieldService.getFieldValueFromCrmObject(this._activity(), SystemFieldIds.ActivityEffectiveDate)
          ?.dateValue ?? null,
    };
    const callRecordingUrl = this.getFieldData(this._activity(), StandardIds.ActivityCallRecordingUrl);
    if (callRecordingUrl) {
      cardInfo.callRecordingUrl = callRecordingUrl;
    }
    return cardInfo;
  });

  private getFieldData(activity: ActivityInterface, fieldId: string): string {
    return this.fieldService.getFieldValueFromCrmObject(activity, fieldId)?.stringValue ?? '';
  }

  constructor(
    private readonly http: HttpClient,
    private readonly fieldService: CrmFieldService,
  ) {
    this.callRecordingSource$ = toObservable(this.cardInfo).pipe(
      switchMap((cardInfo) => {
        if (!cardInfo.callRecordingUrl) {
          return of(null);
        }
        return this.http.get(cardInfo.callRecordingUrl, {
          responseType: 'text',
          withCredentials: true,
        });
      }),
    );
  }
}

interface ActivityCardInformation {
  note: string;
  outcome: string;
  status: string;
  direction: string;
  callRecordingUrl?: string;
  date: Date;
}
